/*
================================================================================
FILE HEADER — apps/studio-web/src/components/StepEditor.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed         |
|----|------------|-------------------|--------------------------|----------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Step Locator Editor      | Created — inline     |
|    |            |                   | (Option D)               | candidate list editor|
|    |            |                   |                          | with add/edit/delete |
|    |            |                   |                          | reorder via Try First|

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/components/StepList.tsx

EXPORTS COVERED
---------------
- StepEditor — inline expansion panel that appears below a step row when
  the user clicks ▶. Shows the label field and ordered candidate list.

IMPORTANT POINTS
----------------
1. "↑ Try First" promotes a candidate to position 0 (becomes the primary).
   The demoted old-first becomes position 1. All other positions shift up.
2. Inline candidate editing: clicking [Edit] replaces the value with an input.
   Pressing Enter/Escape or clicking Save/Cancel confirms/discards.
3. "+ Add Candidate" row appears at the bottom. Strategy is a dropdown,
   value is a text input. addedBy is always 'user'.
4. The edit callback (onEditCandidates) returns the FULL new candidate array.
   App.tsx replaces step.selector.candidates with it.
5. Steps without a selector (openUrl, wait, script, etc.) show only the
   label editor — no candidate section.
================================================================================
*/

import { useState } from 'react';
import type { IRStep, Candidate, CandidateStrategy } from '@plex/ir';
import { newId } from '@plex/ir';

const STRATEGY_OPTIONS: Array<{ value: CandidateStrategy; label: string; color: string }> = [
  { value: 'a11yId',       label: 'a11yId',       color: '#22c55e' },
  { value: 'text',         label: 'text',         color: '#22c55e' },
  { value: 'textContains', label: 'textContains', color: '#eab308' },
  { value: 'contentDesc',  label: 'contentDesc',  color: '#22c55e' },
  { value: 'className',    label: 'className',    color: '#22c55e' },
  { value: 'relative',     label: 'relative',     color: '#eab308' },
  { value: 'css',          label: 'css',          color: '#ef4444' },
  { value: 'xpath',        label: 'xpath',        color: '#ef4444' },
];

const STRATEGY_COLOR: Record<string, string> = Object.fromEntries(
  STRATEGY_OPTIONS.map(s => [s.value, s.color])
);

type Props = {
  step:    IRStep;
  onSave:  (label: string, candidates: Candidate[]) => void;
  onClose: () => void;
};

export function StepEditor({ step, onSave, onClose }: Props) {
  const selector = ('selector' in step && step.selector) ? step.selector as { description: string; candidates: Candidate[] } : null;

  const [label,      setLabel]      = useState<string>((step as { label?: string }).label ?? '');
  const [candidates, setCandidates] = useState<Candidate[]>(selector?.candidates ?? []);
  const [editingIdx,  setEditingIdx]  = useState<number | null>(null);
  const [editValue,   setEditValue]   = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addStrategy, setAddStrategy] = useState<CandidateStrategy>('css');
  const [addValue,    setAddValue]    = useState('');

  const commit = () => onSave(label, candidates);

  // ── Candidate operations ──────────────────────────────────────────────────

  const promoteToFirst = (idx: number) => {
    if (idx === 0) return;
    const arr  = [...candidates];
    const [item] = arr.splice(idx, 1);
    arr.unshift({ ...item!, addedBy: 'user' });
    setCandidates(arr);
  };

  const deleteCandidate = (idx: number) => {
    setCandidates(prev => prev.filter((_, i) => i !== idx));
  };

  const startEdit = (idx: number) => {
    setEditingIdx(idx);
    setEditValue(candidates[idx]?.value ?? '');
  };

  const saveEdit = () => {
    if (editingIdx === null) return;
    setCandidates(prev => prev.map((c, i) => i === editingIdx ? { ...c, value: editValue, addedBy: 'user' } : c));
    setEditingIdx(null);
  };

  const addCandidate = () => {
    const val = addValue.trim();
    if (!val) return;
    setCandidates(prev => [...prev, { strategy: addStrategy, value: val, addedBy: 'user' }]);
    setAddValue('');
    setShowAddForm(false);
  };

  return (
    <div style={{
      background: '#111113',
      borderTop: '1px solid #27272a',
      padding: '12px 16px 14px',
    }}>

      {/* ── Label field ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: selector ? 14 : 0 }}>
        <span style={{ fontSize: 11, color: '#71717a', minWidth: 60 }}>Step label</span>
        <input
          autoFocus
          value={label}
          onChange={e => setLabel(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') onClose(); }}
          placeholder="Step label…"
          style={{ flex: 1, background: '#09090b', border: '1px solid #3f3f46', borderRadius: 5, padding: '4px 8px', color: '#e4e4e7', fontSize: 12, outline: 'none' }}
        />
        <button onClick={commit}  style={smallBtn('#7c3aed', '#fff')}>Save</button>
        <button onClick={onClose} style={smallBtn('#27272a', '#a1a1aa')}>Cancel</button>
      </div>

      {/* ── Candidate list (only for steps with selectors) ── */}
      {selector && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#52525b', letterSpacing: '0.08em', marginBottom: 6 }}>
            LOCATOR CANDIDATES <span style={{ color: '#3f3f46', fontWeight: 400 }}>— tried in order ①②③…</span>
          </div>

          {candidates.map((c, i) => (
            <div key={i}>
              {editingIdx === i ? (
                /* ── Inline edit row ── */
                <div style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
                  <StrategyBadge strategy={c.strategy} />
                  <input
                    autoFocus
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingIdx(null); }}
                    style={{ flex: 1, background: '#09090b', border: '1px solid #7c3aed', borderRadius: 4, padding: '3px 7px', color: '#e4e4e7', fontSize: 11, outline: 'none' }}
                  />
                  <button onClick={saveEdit}            style={tinyBtn('#7c3aed', '#fff')}>Save</button>
                  <button onClick={() => setEditingIdx(null)} style={tinyBtn('#27272a', '#a1a1aa')}>✕</button>
                </div>
              ) : (
                /* ── Normal candidate row ── */
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 0', marginBottom: 2,
                }}>
                  <span style={{ fontSize: 10, color: i === 0 ? '#22c55e' : '#52525b', minWidth: 14, textAlign: 'center' }}>
                    {i === 0 ? '●' : '○'}
                  </span>
                  <StrategyBadge strategy={c.strategy} />
                  <span style={{
                    flex: 1, fontSize: 11, color: '#a1a1aa',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontFamily: 'monospace',
                  }} title={c.value}>{c.value}</span>
                  {c.addedBy === 'heal' && (
                    <span style={{ fontSize: 9, color: '#f59e0b', border: '1px solid #78350f', borderRadius: 3, padding: '1px 4px' }}>healed</span>
                  )}
                  <button onClick={() => startEdit(i)}     style={iconBtn} title="Edit value">✏</button>
                  {i > 0 && (
                    <button onClick={() => promoteToFirst(i)} style={{ ...iconBtn, color: '#7c3aed' }} title="Promote to primary">↑ First</button>
                  )}
                  {candidates.length > 1 && (
                    <button onClick={() => deleteCandidate(i)} style={{ ...iconBtn, color: '#52525b' }}
                      onMouseEnter={e => { (e.currentTarget).style.color = '#ef4444'; }}
                      onMouseLeave={e => { (e.currentTarget).style.color = '#52525b'; }}
                      title="Remove candidate">✕</button>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* ── Add candidate row ── */}
          {showAddForm ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
              <select
                value={addStrategy}
                onChange={e => setAddStrategy(e.target.value as CandidateStrategy)}
                style={{ background: '#09090b', border: '1px solid #3f3f46', borderRadius: 4, padding: '3px 6px', color: '#e4e4e7', fontSize: 11, outline: 'none' }}
              >
                {STRATEGY_OPTIONS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <input
                autoFocus
                value={addValue}
                onChange={e => setAddValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addCandidate(); if (e.key === 'Escape') setShowAddForm(false); }}
                placeholder="value…"
                style={{ flex: 1, background: '#09090b', border: '1px solid #7c3aed', borderRadius: 4, padding: '3px 7px', color: '#e4e4e7', fontSize: 11, outline: 'none' }}
              />
              <button onClick={addCandidate}          style={tinyBtn('#7c3aed', '#fff')}>Add</button>
              <button onClick={() => setShowAddForm(false)} style={tinyBtn('#27272a', '#a1a1aa')}>✕</button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              style={{ marginTop: 6, background: 'none', border: '1px dashed #3f3f46', borderRadius: 5, padding: '3px 10px', color: '#52525b', cursor: 'pointer', fontSize: 11 }}
              onMouseEnter={e => { (e.currentTarget).style.borderColor = '#7c3aed'; (e.currentTarget).style.color = '#7c3aed'; }}
              onMouseLeave={e => { (e.currentTarget).style.borderColor = '#3f3f46'; (e.currentTarget).style.color = '#52525b'; }}
            >
              + Add candidate
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StrategyBadge({ strategy }: { strategy: string }) {
  const color = STRATEGY_COLOR[strategy] ?? '#71717a';
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, color, border: `1px solid ${color}`,
      borderRadius: 3, padding: '1px 5px', flexShrink: 0, fontFamily: 'monospace',
    }}>
      {strategy}
    </span>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

function smallBtn(bg: string, color: string): React.CSSProperties {
  return { background: bg, border: 'none', borderRadius: 5, padding: '4px 10px', color, cursor: 'pointer', fontSize: 11, fontWeight: 600, flexShrink: 0 };
}
function tinyBtn(bg: string, color: string): React.CSSProperties {
  return { background: bg, border: 'none', borderRadius: 4, padding: '2px 7px', color, cursor: 'pointer', fontSize: 10, fontWeight: 600, flexShrink: 0 };
}
const iconBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', padding: '1px 5px', fontSize: 11, flexShrink: 0,
};
